/**
 * Контракт задач colver (`/workspaces/{ws}/tasks`, docs/BACKEND_CONTRACTS.md)
 * + адаптеры к фронтовой сущности `Task`.
 *
 * Маппинг статусов: backend `in-progress` ↔ frontend `in_progress`,
 * остальные значения совпадают.
 */
/** Нормализованные данные задач, возвращаемые API-адаптером. */
export type TaskStatus =
  | 'planned'
  | 'queued'
  | 'in_progress'
  | 'paused'
  | 'partial'
  | 'done';

export type TaskPriority = 'high' | 'medium' | 'low';

export interface TaskComment {
  id: string;
  author: string;
  authorUserSid?: string;
  updatedAt?: string | null;
  /** Права комментария независимы от прав редактирования задачи. */
  canEdit?: boolean;
  canDelete?: boolean;
  text: string;
  /** Стабильные ID упомянутых пользователей; legacy может содержать имена. */
  mentions: string[];
  mentionUsers?: TaskParticipant[];
  /** ISO datetime либо `YYYY-MM-DD HH:mm`. */
  createdAt: string;
}

/** Участник задачи в серверном контексте доступа. */
export interface TaskParticipant {
  userSid: string;
  displayName: string;
}

export interface Task {
  id: string;
  title: string;
  text: string | null;
  managerId: string | null;
  manager: string | null;
  priority: TaskPriority;
  status: TaskStatus;
  /** Ссылка на звонок в meerec */
  communicationId: string | null;
  /** ISO date YYYY-MM-DD */
  dueDate: string;
  /** ISO datetime */
  createdAt: string;
  /** ID автора из API. Отсутствующий ID не даёт прав автора. */
  creatorUserSid?: string | null;
  /** Легаси-поле демо-интерфейса; не использовать для проверки прав. */
  creator: 'rop' | 'manager';
  observerUserSid?: string | null;
  permissions?: {
    canEdit: boolean;
    canDelete: boolean;
    canComment: boolean;
    allowedStates: TaskStatus[];
  };
  mentionCandidates?: TaskParticipant[];
  /** Совместимость с legacy, не использовать для проверки видимости. */
  withObserver: boolean;
  /** История комментариев. Пусто по умолчанию. */
  comments: TaskComment[];
}

export interface CreateTaskInput {
  observerUserSid?: string;
  idempotencyKey?: string;
  title: string;
  text?: string;
  managerId?: string;
  priority: TaskPriority;
  /** Дефолт `planned` если не задано (используется кнопкой `+ Добавить` в колонке канбана). */
  status?: TaskStatus;
  dueDate: string;
  communicationId?: string;
}

export interface UpdateTaskInput {
  title?: string;
  text?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  dueDate?: string;
  managerId?: string | null;
  manager?: string | null;
  communicationId?: string | null;
  comments?: TaskComment[];
}

/** Статус задачи в мнемониках бэка (kebab-case). */
export type BackendTaskState =
  | 'planned'
  | 'queued'
  | 'in-progress'
  | 'paused'
  | 'partial'
  | 'done';

/** Комментарий задачи (контракт бэка). */
export interface BackendTaskComment {
  sid: string;
  authorUserSid: string;
  text: string;
  mentions?: string[];
  mentionUsers?: TaskParticipant[];
  createdAt: string;
  updatedAt?: string | null;
  canEdit?: boolean;
  canDelete?: boolean;
}

/** Задача (контракт бэка). */
export interface BackendTask {
  sid: string;
  workspaceId: string;
  title: string;
  text?: string | null;
  managerUserSid?: string | null;
  creatorUserSid?: string | null;
  communicationId?: string | null;
  priority: TaskPriority;
  state: BackendTaskState;
  dueDate?: string | null;
  withObserver?: boolean;
  observerUserSid?: string | null;
  permissions?: {
    canEdit: boolean;
    canDelete: boolean;
    canComment: boolean;
    allowedStates: BackendTaskState[];
  };
  mentionCandidates?: TaskParticipant[];
  comments?: BackendTaskComment[];
  createdAt: string;
  updatedAt?: string | null;
}

/** Тело создания задачи (`PUT /workspaces/{ws}/tasks`). */
export interface BackendCreateTaskInput {
  observerUserSid?: string;
  title: string;
  text?: string;
  managerUserSid?: string;
  communicationId?: string;
  priority?: TaskPriority;
  state?: BackendTaskState;
  dueDate?: string | null;
}

/** Тело частичного обновления (`PATCH /workspaces/{ws}/tasks/{sid}`). */
export interface BackendUpdateTaskInput {
  title?: string;
  text?: string | null;
  managerUserSid?: string | null;
  communicationId?: string | null;
  priority?: TaskPriority;
  state?: BackendTaskState;
  dueDate?: string | null;
}

/** Тело добавления комментария. */
export interface AddTaskCommentInput {
  text: string;
  mentions?: string[];
}

/** PATCH комментария: отсутствие mentions сохраняет адресатов, [] удаляет всех. */
export type UpdateTaskCommentInput = AddTaskCommentInput;

/** Backend state → frontend TaskStatus. */
export const taskStatusFromState = (state: BackendTaskState): TaskStatus =>
  state === 'in-progress' ? 'in_progress' : state;

/** Frontend TaskStatus → backend state. */
export const taskStateFromStatus = (status: TaskStatus): BackendTaskState =>
  status === 'in_progress' ? 'in-progress' : status;

/** Резолвер имени по userSid (members воркспейса). */
export type UserNameResolver = (
  userSid: string | null | undefined,
) => string | null;

/** Адаптация задачи бэка к фронтовой сущности. */
export const adaptBackendTask = (
  raw: BackendTask,
  resolveName: UserNameResolver,
): Task => {
  // Имена уже разрешённых участников из detail; общий roster для этого не нужен.
  const participantName: UserNameResolver = (userSid) =>
    raw.mentionCandidates?.find((person) => person.userSid === userSid)
      ?.displayName || resolveName(userSid);
  return {
    id: raw.sid,
    title: raw.title,
    text: raw.text ?? null,
    managerId: raw.managerUserSid ?? null,
    manager: participantName(raw.managerUserSid),
    priority: raw.priority,
    status: taskStatusFromState(raw.state),
    communicationId: raw.communicationId ?? null,
    dueDate: raw.dueDate ?? '',
    createdAt: raw.createdAt,
    creatorUserSid: raw.creatorUserSid ?? null,
    // Легаси-поле демо; право автора определяется только по creatorUserSid.
    creator: 'rop',
    withObserver: Boolean(raw.observerUserSid),
    observerUserSid: raw.observerUserSid ?? null,
    permissions: raw.permissions
      ? {
          ...raw.permissions,
          allowedStates: raw.permissions.allowedStates.map(taskStatusFromState),
        }
      : {
          canEdit: false,
          canDelete: false,
          canComment: false,
          allowedStates: [],
        },
    mentionCandidates: raw.mentionCandidates ?? [],
    comments: (raw.comments ?? []).map(
      (c): TaskComment => ({
        id: c.sid,
        author: participantName(c.authorUserSid) ?? '—',
        authorUserSid: c.authorUserSid,
        updatedAt: c.updatedAt ?? null,
        canEdit: c.canEdit === true,
        canDelete: c.canDelete === true,
        text: c.text,
        mentions: c.mentions ?? [],
        mentionUsers: c.mentionUsers ?? [],
        createdAt: c.createdAt,
      }),
    ),
  };
};

/** Ограниченная страница доступных задач и контекст создания. */
export interface TaskListInput {
  filter?: {
    communicationIds?: string[];
    analyticsInsightSid?: string;
    managerUserSid?: string;
    states?: BackendTaskState[];
  };
  limit?: number;
  cursor?: string;
  include?: ('creation-context' | 'source-summary')[];
}
export interface TaskCreationContext {
  assignees: TaskParticipant[];
  observer: TaskParticipant | null;
}
export interface BackendTaskPage {
  rows: BackendTask[];
  nextCursor: string | null;
  creationContext?: TaskCreationContext;
  sourceSummaries?: {
    communicationId: string;
    count: number;
    tasks: BackendTask[];
  }[];
}
